import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from 'store/middleware/api/api';
import { resultViewMode } from 'src/enums/compare-estimates';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { toast } from '@oliasoft-open-source/react-ui-library';
import { ICompare, IEstimateItem } from 'src/common/interfaces/compare-estimates.interfaces';
import { IDesignItem } from 'src/common/interfaces/campaigns.interfaces';

interface ICompareEstimateState {
  showCompareModal: boolean;
  showCompareEstimatesModal: boolean;
  isAdding: boolean;
  isFetching: boolean;
  isRecalculating: boolean;
  isGeneratingEstimates: boolean;
  viewMode: (typeof resultViewMode)[keyof typeof resultViewMode];
  designs: IDesignItem[];
  list: ICompare[];
  estimates: IEstimateItem[];
  estimatesList: [];
  countries: [];
  simulation: any;
}

const initialCompareEstimatesState: ICompareEstimateState = {
  showCompareModal: false,
  showCompareEstimatesModal: false,
  isAdding: false,
  isFetching: false,
  isRecalculating: false,
  isGeneratingEstimates: false,
  viewMode: resultViewMode.COMBINED,
  designs: [],
  list: [],
  estimates: [],
  estimatesList: [],
  countries: [],
  simulation: {},
};

const compareEstimates = createSlice({
  name: 'compareEstimates',
  initialState: initialCompareEstimatesState,
  reducers: {
    compareDataRequested: (compareEstimates) => {
      compareEstimates.isFetching = true;
    },
    compareDataFetched: (compareEstimates, action) => {
      compareEstimates.isFetching = false;
      compareEstimates.designs = action.payload.designs;
    },
    compareDataRequestFailed: (compareEstimates) => {
      compareEstimates.isFetching = false;
    },
    compareRequested: (compareEstimates) => {
      compareEstimates.isGeneratingEstimates = true;
      compareEstimates.estimates = [];
      compareEstimates.simulation = {};
      compareEstimates.estimatesList = [];
    },
    compareFetched: (compareEstimates, action) => {
      const { compareList, estimates, estimatesList, jobIds } = action.payload;
      compareEstimates.list = compareList.map((compare: ICompare) => {
        compare.active = compare.compareId === action.payload.compare.compareId;
        return compare;
      });
      const shouldGetResult =
        jobIds.length > 0 && jobIds.some((id: string) => compareEstimates.simulation[id] !== 'Finished');
      compareEstimates.estimates = estimates;
      compareEstimates.estimatesList = estimatesList;
      compareEstimates.isRecalculating = shouldGetResult;
      compareEstimates.isGeneratingEstimates = false;
      compareEstimates.showCompareModal = false;
    },
    compareRequestFailed: (compareEstimates) => {
      compareEstimates.isGeneratingEstimates = false;
      compareEstimates.isRecalculating = false;
    },
    updateIsRecalculating: (compareEstimates, action) => {
      compareEstimates.isRecalculating = action.payload;
    },
    showCompareModalUpdated: (compareEstimates, action) => {
      compareEstimates.showCompareModal = action.payload;
    },
    compareListRequested: (compareEstimates) => {
      compareEstimates.isFetching = true;
    },
    compareListFetched: (compareEstimates, action) => {
      compareEstimates.list = action.payload.compareList;
      compareEstimates.estimates = action.payload.estimates.map((estimate: IEstimateItem) => ({
        ...estimate,
        visible: true,
      }));
      compareEstimates.estimatesList = action.payload.estimatesList;
      compareEstimates.countries = action.payload.countries;
      compareEstimates.isFetching = false;
    },
    compareListRequestFailed: (compareEstimates) => {
      compareEstimates.isFetching = false;
    },
    compareSelected: (compareEstimates, action) => {
      const { list } = compareEstimates;
      compareEstimates.viewMode = resultViewMode.COMBINED;
      compareEstimates.list = list.map((compare) => {
        compare.active = compare.compareId === action.payload;
        return compare;
      });
    },

    addSimulationStatus: (compareEstimates, action) => {
      const { notification } = action.payload;
      compareEstimates.simulation[notification.metadata.jobId] = notification.status;
    },

    viewModeUpdated: (compareEstimates, action) => {
      compareEstimates.viewMode = action.payload;
    },
    compareRemoved: (compareEstimates, action) => {
      const { list } = compareEstimates;
      const index = list.findIndex((compare) => compare.compareId === action.payload.id);
      delete list[index];
      compareEstimates.list = list.filter((i) => i);
    },
    compareUpdateRequested: (compareEstimates) => {
      compareEstimates.isAdding = true;
    },
    compareUpdated: (compareEstimates, action) => {
      const { compare: updatedCompare, estimates: updatedEstimates } = action.payload;
      const { list, estimates } = compareEstimates;
      const index = list.findIndex((compare) => compare.compareId === updatedCompare.compareId);
      if (index !== -1) {
        action.payload.compare.active = true;
        compareEstimates.list[index] = updatedCompare;
        if (updatedEstimates?.length) {
          const filteredEstimates = estimates.filter((e) => e.compareId !== updatedCompare.compareId);
          compareEstimates.estimates = [...filteredEstimates, ...updatedEstimates];
        }
      }
      compareEstimates.isAdding = false;
    },
    compareUpdateRequestFailed: (compareEstimates) => {
      compareEstimates.isAdding = false;
    },
    compareAddRequested: (compareEstimates) => {
      compareEstimates.isAdding = true;
    },
    compareAdded: (compareEstimates, action) => {
      compareEstimates.list.forEach((estimate) => (estimate.active = false));
      compareEstimates.list.push({ ...action.payload, active: true });
      compareEstimates.isAdding = false;
    },
    compareAddRequestFailed: (compareEstimates) => {
      compareEstimates.isAdding = false;
    },
    compareItemRemoved: (compareEstimates, action) => {
      const { compareId, designs, projectId } = action.payload;
      const { estimates } = compareEstimates;
      const compareIdx = compareEstimates.list.findIndex((item) => item.compareId === compareId);
      compareEstimates.list[compareIdx].designs = designs;
      compareEstimates.estimates = estimates.filter(
        (estimate) => !(estimate.projectId === projectId && estimate.compareId === compareId),
      );
    },
    compareResultRequested: (compareEstimates) => {
      compareEstimates.simulation = {};
    },
    compareResultFetched: (compareEstimates, action) => {
      compareEstimates.estimates = action.payload;
      compareEstimates.simulation = {};
      compareEstimates.isRecalculating = false;
    },
    compareResultFailed: (compareEstimates) => {
      compareEstimates.simulation = {};
      compareEstimates.isRecalculating = false;
    },
  },
});

export const {
  compareDataRequested,
  compareDataFetched,
  compareDataRequestFailed,
  compareRequested,
  compareFetched,
  compareRequestFailed,
  showCompareModalUpdated,
  compareListRequested,
  compareListFetched,
  compareListRequestFailed,
  compareSelected,
  addSimulationStatus,
  viewModeUpdated,
  compareRemoved,
  compareUpdated,
  compareUpdateRequested,
  compareUpdateRequestFailed,
  compareAddRequested,
  compareAdded,
  compareAddRequestFailed,
  compareItemRemoved,
  updateIsRecalculating,
  compareResultRequested,
  compareResultFetched,
  compareResultFailed,
} = compareEstimates.actions;
export default compareEstimates.reducer;

export const initialCompare = {
  name: i18n.t(translations.compare_newComparison),
};

// Get compare data
export const getCompareData = (companyId: string) =>
  apiCallBegan({
    url: `/api/compare-estimates/run-data/${companyId}`,
    method: 'GET',
    onStart: compareDataRequested.type,
    onSuccess: (response) => ({
      type: compareDataFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.unexpectedError)}`,
        },
      });
      return {
        type: compareDataRequestFailed.type,
      };
    },
  });

// Run concept compare
export const runConceptCompare = (data: {
  compare: ICompare;
  reuseSimulations: string[];
  templates: { datasetId: number; templateId: string }[];
}) =>
  apiCallBegan({
    url: `/api/compare-estimates/concept`,
    method: 'POST',
    data,
    onStart: compareRequested.type,
    onSuccess: (response) => ({
      type: compareFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.compare_failedToRunConceptCompare)}`,
        },
      });
      return {
        type: compareRequestFailed.type,
      };
    },
  });

// Function to retrieve a list of compare estimates for a given company ID.
export const getCompareList = (companyId: string) =>
  apiCallBegan({
    url: `/api/compare-estimates/${companyId}`,
    method: 'GET',
    onStart: compareListRequested.type,
    onSuccess: compareListFetched.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.compare_failedToGetCompareList)}`,
        },
      });
      return {
        type: compareListRequestFailed.type,
      };
    },
  });

// Removes the compare estimate with the given ID.
export const removeCompare = (id: string) =>
  apiCallBegan({
    url: `/api/compare-estimates/${id}`,
    method: 'DELETE',
    onSuccess: () => ({
      type: compareRemoved.type,
      payload: { id },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.failedToRemove)}`,
        },
      });
    },
  });

// Updates the compare estimate with the given data.
export const updateCompare = (data: ICompare) =>
  apiCallBegan({
    url: `/api/compare-estimates/${data.compareId}`,
    method: 'POST',
    data,
    onStart: compareUpdateRequested.type,
    onSuccess: compareUpdated.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.failedToUpdate)}`,
        },
      });
      return {
        type: compareUpdateRequestFailed.type,
      };
    },
  });

// Add an intial empty compare
export const addInitialCompare = (companyId: string) =>
  apiCallBegan({
    url: `/api/compare`,
    method: 'POST',
    data: { ...initialCompare, companyId },
    onStart: compareAddRequested.type,
    onSuccess: compareAdded.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.compare_failedToAddInitialCompare)}`,
        },
      });
      return compareAddRequestFailed.type;
    },
  });

// Removes the compare estimate item with the given compareId and projectId.
export const removeCompareItem = (data: { compare: ICompare; projectId: string; datasetId: string | number }) =>
  apiCallBegan({
    url: `/api/compare-estimates/remove-item`,
    method: 'DELETE',
    data,
    onSuccess: compareItemRemoved.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.failedToRemove)}`,
        },
      });
    },
  });

export const getCompareResult = () =>
  apiCallBegan({
    url: `/api/compare-estimates/result`,
    method: 'GET',
    onStart: compareResultRequested.type,
    onSuccess: compareResultFetched.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.compare_failedToGetSimulationData)}`,
        },
      });
      return {
        type: compareResultFailed.type,
      };
    },
  });
